import type { CopilotCoachingState, CopilotSessionState, PlaybookVersionStatus, PostCall, PostCallCrmWriteback, StepState, SuggestionType } from '@lia/contracts';
import type { StatusTone } from '../primitives/badge.js';

// Live Call Copilot: semantic tones and Spanish labels not already in '@lia/contracts' (addendum §5–§11).
// Every status is rendered with icon + text (StatusBadge); color is never the only cue.

export const COPILOT_SESSION_STATE_LABELS: Record<CopilotSessionState, string> = {
  waiting: 'Esperando llamada',
  live: 'En llamada',
  ended: 'Terminada',
};
export const COPILOT_SESSION_STATE_TONE: Record<CopilotSessionState, StatusTone> = { waiting: 'info', live: 'accent', ended: 'neutral' };

export const COPILOT_COACHING_TONE: Record<CopilotCoachingState, StatusTone> = {
  active: 'success',
  needs_selection: 'warning',
  unavailable: 'danger',
  paused: 'neutral',
  budget_exceeded: 'warning',
};

export const STEP_STATE_TONE: Record<StepState, StatusTone> = {
  not_started: 'neutral',
  in_progress: 'info',
  covered: 'success',
  needs_verification: 'warning',
  not_applicable: 'neutral',
};

export const SUGGESTION_TYPE_TONE: Record<SuggestionType, StatusTone> = {
  answer: 'info',
  objection: 'warning',
  next_question: 'accent',
  reminder: 'neutral',
  escalation: 'danger',
};

export type SuggestionStatus = 'active' | 'superseded' | 'dismissed' | 'used' | 'discarded_stale' | 'withdrawn';
export const SUGGESTION_STATUS_LABELS: Record<SuggestionStatus, string> = {
  active: 'Vigente',
  superseded: 'Reemplazada',
  dismissed: 'Descartada',
  used: 'Usada',
  discarded_stale: 'Obsoleta',
  withdrawn: 'Retirada',
};
export const SUGGESTION_STATUS_TONE: Record<SuggestionStatus, StatusTone> = {
  active: 'accent',
  superseded: 'neutral',
  dismissed: 'neutral',
  used: 'success',
  discarded_stale: 'neutral',
  withdrawn: 'neutral',
};

export const SOURCE_KIND_LABELS = {
  document: 'Documento',
  project: 'Proyecto',
  unit: 'Unidad',
  price: 'Precio',
  availability: 'Disponibilidad',
  playbook: 'Playbook',
  contact: 'Contacto',
} as const;

export type SuggestionVerification = 'verified' | 'incomplete' | 'needs_confirmation';
export const VERIFICATION_TONE: Record<SuggestionVerification, StatusTone> = { verified: 'success', incomplete: 'warning', needs_confirmation: 'warning' };

export const PLAYBOOK_VERSION_STATUS_TONE: Record<PlaybookVersionStatus, StatusTone> = { draft: 'neutral', tested: 'info', published: 'success', archived: 'neutral' };

export type PostCallStatus = PostCall['status'];
export const POST_CALL_STATUS_LABELS: Record<PostCallStatus, string> = {
  none: 'Sin borrador',
  pending: 'Pendiente',
  ready: 'Lista para revisar',
  unavailable: 'No disponible',
  reviewed: 'Revisada',
};
export const POST_CALL_STATUS_TONE: Record<PostCallStatus, StatusTone> = { none: 'neutral', pending: 'info', ready: 'warning', unavailable: 'neutral', reviewed: 'success' };

/** Realtime connection of this viewer to the session stream (not the telephone call). */
export type CopilotConnectionState = 'connecting' | 'live' | 'reconnecting' | 'resyncing' | 'offline' | 'closed';
export const COPILOT_CONNECTION_LABELS: Record<CopilotConnectionState, string> = {
  connecting: 'Conectando…',
  live: 'Conectado en vivo',
  reconnecting: 'Reconectando…',
  resyncing: 'Recuperando el estado…',
  offline: 'Sin conexión',
  closed: 'Desconectado',
};
export const COPILOT_CONNECTION_TONE: Record<CopilotConnectionState, StatusTone> = {
  connecting: 'info',
  live: 'success',
  reconnecting: 'warning',
  resyncing: 'info',
  offline: 'danger',
  closed: 'neutral',
};

/** Transcript freshness during a call: «retrasada» after N seconds without new speech. */
export type TranscriptFreshness = 'waiting' | 'flowing' | 'delayed' | 'ended' | 'hidden';
export const TRANSCRIPT_FRESHNESS_LABELS: Record<TranscriptFreshness, string> = {
  waiting: 'Esperando transcripción',
  flowing: 'Transcripción al día',
  delayed: 'Transcripción retrasada',
  ended: 'Transcripción cerrada',
  hidden: 'Sin acceso a la transcripción',
};
export const TRANSCRIPT_FRESHNESS_TONE: Record<TranscriptFreshness, StatusTone> = { waiting: 'info', flowing: 'success', delayed: 'warning', ended: 'neutral', hidden: 'neutral' };

export const SPEAKER_LABELS = { internal: 'Asesor', external: 'Cliente', unknown: 'Sin identificar' } as const;

export const POST_CALL_CRM_WRITEBACK_TONE: Record<PostCallCrmWriteback['status'], StatusTone> = { none: 'neutral', pending: 'info', written: 'success', skipped: 'neutral', failed: 'danger' };
